import React, { useCallback, useEffect, useState } from 'react';
import { fetchScriptRuns } from './adminApi';

function when(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function duration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const total = Number(seconds);
  if (total < 60) return `${total.toFixed(1)}s`;
  const minutes = Math.floor(total / 60);
  return `${minutes}m ${Math.round(total % 60)}s`;
}

function ago(value) {
  if (!value) return '';
  const hours = (Date.now() - new Date(value).getTime()) / 3600000;
  if (hours < 1) return 'less than an hour ago';
  if (hours < 48) return `${Math.round(hours)} hours ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function StatusPill({ status }) {
  return <span className={`admin-pill admin-pill--${status}`}>{status}</span>;
}

function RunRow({ run }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr>
        <td>{run.script_name}</td>
        <td><StatusPill status={run.status} /></td>
        <td>{when(run.started_at)}</td>
        <td>{duration(run.duration_seconds)}</td>
        <td>{(run.steps || []).length}</td>
        <td>
          {run.error_type ? (
            <span className="admin-error-inline" title={run.error_message}>
              {run.error_type}: {run.error_message}
            </span>
          ) : '—'}
        </td>
        <td className="admin-actions">
          <button type="button" className="admin-button admin-button--link" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Details'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="admin-run-detail">
            <h5>Steps</h5>
            {(run.steps || []).length === 0 ? (
              <p className="admin-meta-line">No steps recorded.</p>
            ) : (
              <table className="admin-table admin-table--compact">
                <thead>
                  <tr>
                    <th scope="col">Step</th>
                    <th scope="col">Status</th>
                    <th scope="col">Duration</th>
                    <th scope="col">Error</th>
                    <th scope="col">Metrics</th>
                  </tr>
                </thead>
                <tbody>
                  {run.steps.map((step) => (
                    <tr key={step.id}>
                      <td>{step.step_name}</td>
                      <td><StatusPill status={step.status} /></td>
                      <td>{duration(step.duration_seconds)}</td>
                      <td>{step.error_type ? `${step.error_type}: ${step.error_message}` : '—'}</td>
                      <td>
                        {step.metrics && Object.keys(step.metrics).length > 0
                          ? <code>{JSON.stringify(step.metrics)}</code>
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="admin-meta-line">
              Dyno: <code>{run.dyno || 'local'}</code>
              {run.git_sha ? <> · commit <code>{run.git_sha.slice(0, 8)}</code></> : null}
            </p>
            <h5>Log tail</h5>
            <pre className="admin-log-tail">{run.log_tail || 'No log output captured.'}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

function AdminScriptRuns() {
  const [script, setScript] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchScriptRuns({ script, status, limit: 50 }, signal));
    } catch (loadError) {
      if (loadError.name !== 'AbortError') setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [script, status]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  if (data?.migration_pending) {
    return (
      <p className="admin-error" role="alert">
        The <code>script_run_log</code> table does not exist yet. Apply
        {' '}<code>migrations/create_admin_console_tables.sql</code> to this database.
      </p>
    );
  }

  const latest = data?.latest || [];
  const reported = new Set(latest.map((run) => run.script_name));
  const neverRun = (data?.known_scripts || []).filter((name) => !reported.has(name));

  return (
    <div className="admin-panel">
      <h3 className="admin-section-title">Last run per script</h3>
      <div className="admin-card-grid">
        {latest.map((run) => (
          <div key={run.script_name} className={`admin-run-card admin-run-card--${run.status}`}>
            <h4>{run.script_name}</h4>
            <StatusPill status={run.status} />
            <p className="admin-run-card-line">{when(run.started_at)}</p>
            <p className="admin-card-sub">{ago(run.started_at)} · {duration(run.duration_seconds)}</p>
            {run.status === 'running' && !run.finished_at && (
              <p className="admin-error-inline">
                Started but never finished — the dyno may have died mid-run.
              </p>
            )}
            {run.error_type && <p className="admin-error-inline">{run.error_type}</p>}
          </div>
        ))}
        {neverRun.map((name) => (
          <div key={name} className="admin-run-card admin-run-card--unknown">
            <h4>{name}</h4>
            <span className="admin-pill admin-pill--unknown">no runs</span>
            <p className="admin-card-sub">
              Has not reported a run. Either it is not scheduled, or it has not run since
              run logging was deployed.
            </p>
          </div>
        ))}
      </div>

      <h3 className="admin-section-title">Run history</h3>
      <div className="admin-toolbar">
        <select className="admin-input" value={script} onChange={(event) => setScript(event.target.value)} aria-label="Script">
          <option value="">All scripts</option>
          {(data?.known_scripts || []).map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <select className="admin-input" value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Status">
          <option value="">Any status</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
        </select>
      </div>

      {error && <p className="admin-error" role="alert">{error}</p>}
      {loading && <p className="admin-meta-line">Loading…</p>}

      <div className="admin-table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Script</th>
              <th scope="col">Status</th>
              <th scope="col">Started</th>
              <th scope="col">Duration</th>
              <th scope="col">Steps</th>
              <th scope="col">Error</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {(data?.runs || []).length === 0 && !loading && (
              <tr><td colSpan={7} className="admin-empty">No runs recorded yet.</td></tr>
            )}
            {(data?.runs || []).map((run) => <RunRow key={run.id} run={run} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminScriptRuns;
